import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import { useAuth } from "./AuthContext";

const NotificationsContext = createContext(null);

const DEFAULT_POLL_INTERVAL_MS = 45_000;
const DEFAULT_PAGE_LIMIT = 20;

const safeGetPathname = (urlOrPath) => {
  if (!urlOrPath) return "";
  // Accept already-a-path
  if (typeof urlOrPath === "string" && urlOrPath.startsWith("/")) return urlOrPath;
  // Accept absolute URL
  try {
    const u = new URL(String(urlOrPath));
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return "";
  }
};

const normalizeDeepLink = (path) => {
  if (!path) return "";
  // If backend still sends legacy need paths, redirect to list+modal
  const legacyNeedMatch = path.match(/^\/client-needs\/(\d+)(?:\/)?$/i);
  if (legacyNeedMatch) {
    return `/portal/asap-pilot/needs?needId=${legacyNeedMatch[1]}`;
  }
  const pilotNeedMatch = path.match(/^\/portal\/asap-pilot\/needs\/(\d+)(?:\/)?$/i);
  if (pilotNeedMatch) {
    return `/portal/asap-pilot/needs?needId=${pilotNeedMatch[1]}`;
  }
  return path;
};

export const NotificationsProvider = ({ children, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS }) => {
  const toast = useToast();
  const { isAuthenticated, user } = useAuth();
  const isConsultantPortal = (user?.portalScope || "internal") === "consultant";

  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [error, setError] = useState(null);

  // Helps avoid spamming "new notification" toasts on every poll.
  const lastToastKeyRef = useRef(null);

  const apiBase = process.env.REACT_APP_BASE_API_URL;

  const resetState = useCallback(() => {
    setIsOpen(false);
    setUnreadCount(0);
    setItems([]);
    setLoadingList(false);
    setLoadingCount(false);
    setError(null);
    lastToastKeyRef.current = null;
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated || isConsultantPortal) return;
    if (!apiBase) return;
    setLoadingCount(true);
    try {
      const res = await axios.get(`${apiBase}/notifications/unread-count`);
      const count = Number(res?.data?.unread_count ?? res?.data?.count ?? res?.data?.unread ?? 0);
      setUnreadCount(Number.isFinite(count) ? count : 0);
      setError(null);
    } catch (e) {
      // Non-blocking: backend might not be deployed yet.
      setError(e);
    } finally {
      setLoadingCount(false);
    }
  }, [apiBase, isAuthenticated, isConsultantPortal]);

  const fetchNotifications = useCallback(
    async ({ status = "all", limit = DEFAULT_PAGE_LIMIT } = {}) => {
      if (!isAuthenticated || isConsultantPortal) return;
      if (!apiBase) return;

      setLoadingList(true);
      try {
        const res = await axios.get(`${apiBase}/notifications`, {
          params: {
            status,
            limit,
          },
        });
        const list = Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : [];
        setItems(list);
        setError(null);

        // If backend returns unread_count in the same payload, keep it in sync.
        const maybeUnread = res?.data?.unread_count;
        if (typeof maybeUnread === "number") setUnreadCount(maybeUnread);

        // High-priority toast for newly arrived notifications.
        const newest = list?.[0];
        const newestKey = newest?.id || newest?.created_at || newest?.createdAt;
        const isHigh = String(newest?.priority || "").toLowerCase() === "high";
        const isUnread = !newest?.read_at && !newest?.readAt;
        if (newestKey && isHigh && isUnread && lastToastKeyRef.current !== newestKey) {
          lastToastKeyRef.current = newestKey;
          toast({
            title: newest?.title || "New notification",
            description: newest?.body || newest?.message || undefined,
            status: "warning",
            duration: 6000,
            isClosable: true,
            position: "top-right",
          });
        }
      } catch (e) {
        setError(e);
      } finally {
        setLoadingList(false);
      }
    },
    [apiBase, isAuthenticated, isConsultantPortal, toast]
  );

  const markRead = useCallback(
    async (id) => {
      if (!id) return;
      if (!isAuthenticated || isConsultantPortal) return;
      if (!apiBase) return;

      // Optimistic update
      setItems((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, (Number(c) || 0) - 1));

      try {
        await axios.patch(`${apiBase}/notifications/${id}`, { read: true });
        setError(null);
      } catch (e) {
        setError(e);
        // fallback: re-sync from server
        refreshUnreadCount();
        fetchNotifications({ status: isOpen ? "all" : "unread" }).catch(() => {});
      }
    },
    [apiBase, fetchNotifications, isAuthenticated, isConsultantPortal, isOpen, refreshUnreadCount]
  );

  const markAllRead = useCallback(async () => {
    if (!isAuthenticated || isConsultantPortal) return;
    if (!apiBase) return;

    // Optimistic
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || now })));
    setUnreadCount(0);

    try {
      await axios.post(`${apiBase}/notifications/mark-all-read`);
      setError(null);
    } catch (e) {
      setError(e);
      refreshUnreadCount();
      fetchNotifications({ status: "all" }).catch(() => {});
    }
  }, [apiBase, fetchNotifications, isAuthenticated, isConsultantPortal, refreshUnreadCount]);

  const openNotifications = useCallback(() => {
    setIsOpen(true);
  }, []);
  const closeNotifications = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Polling: count-only most of the time, list refresh when drawer is open.
  useEffect(() => {
    if (!isAuthenticated || isConsultantPortal) {
      resetState();
      return;
    }

    refreshUnreadCount();
    // When opening portal tab again, refresh quickly.
    const onFocus = () => refreshUnreadCount();
    window.addEventListener("focus", onFocus);

    const intervalId = window.setInterval(() => {
      if (isOpen) {
        fetchNotifications({ status: "all" });
      } else {
        refreshUnreadCount();
      }
    }, pollIntervalMs);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(intervalId);
    };
  }, [
    fetchNotifications,
    isAuthenticated,
    isConsultantPortal,
    isOpen,
    pollIntervalMs,
    refreshUnreadCount,
    resetState,
  ]);

  // When user opens the drawer, eagerly fetch list and sync count.
  useEffect(() => {
    if (!isAuthenticated || isConsultantPortal) return;
    if (!isOpen) return;
    fetchNotifications({ status: "all" });
    refreshUnreadCount();
  }, [fetchNotifications, isAuthenticated, isConsultantPortal, isOpen, refreshUnreadCount]);

  const value = useMemo(
    () => ({
      isOpen,
      openNotifications,
      closeNotifications,
      unreadCount,
      items,
      loadingList,
      loadingCount,
      error,
      refreshUnreadCount,
      fetchNotifications,
      markRead,
      markAllRead,
      // Utility: consistent deep link field
      getDeepLink: (n) => normalizeDeepLink(safeGetPathname(n?.deep_link || n?.deepLink || n?.url)),
    }),
    [
      closeNotifications,
      error,
      fetchNotifications,
      isOpen,
      items,
      loadingCount,
      loadingList,
      markAllRead,
      markRead,
      openNotifications,
      refreshUnreadCount,
      unreadCount,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }
  return ctx;
};
